import { TitleCasePipe, NgIf, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { FuseConfirmationService } from '@fuse/services/confirmation';

import { ApexOptions, NgApexchartsModule } from 'ng-apexcharts';
import { forkJoin, Subject } from 'rxjs';
import { map, filter, distinctUntilChanged, takeUntil, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CourtComponent } from './court/court.component';
import { CourtsService } from './courts.service';
import { VenueService } from '../../shared/services/venue.service';
import { getSportDisplayName } from '../../shared/sport-display';
import { formatAedCurrency } from '../../shared/aed-currency';

@Component({
    selector: 'app-courts',
    templateUrl: './courts.component.html',
    styleUrl: './courts.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatDividerModule,
        NgApexchartsModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatFormFieldModule,
        MatSidenavModule,
        MatTooltipModule,
        CourtComponent,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        NgIf,
        NgClass,
        TitleCasePipe
    ],
})
export class CourtsComponent implements OnInit, OnDestroy {
    readonly formatAedCurrency = formatAedCurrency;
    readonly getSportDisplayName = getSportDisplayName;
    data: any;
    accountBalanceOptions: ApexOptions;
    recentTransactionsDataSource: MatTableDataSource<any> = new MatTableDataSource();
    recentTransactionsTableColumns: string[] = [
        'sportLogo',
        'name',
        'courtType',
        'surfaceType',
        'minCapacity',
        'maxCapacity',
        'pricePerHour',
        'actions'
    ];
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    drawerMode: 'over' | 'side' = 'over';
    drawerOpened: boolean = false;
    venueId = '';
    venue: any = {};
    selectedCourt: any = null;
    isLoading: boolean = true; // Add loading state
    pageIndex = 0;
    pageSize = 10;
    totalItems = 0;
    sortBy = 'name:asc';

    constructor(
        private courtsService: CourtsService,
        private venueService: VenueService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _route: ActivatedRoute,
        private cdr: ChangeDetectorRef
    ) {
        this._route.paramMap.pipe(
            map(pm => pm.get('id')),
            filter((v): v is string => v !== null),
            distinctUntilChanged(),
            takeUntilDestroyed()
        ).subscribe(id => {
            this.venueId = id;
            this.getVenueById(id);
            this.loadAllCourts();
            this.cdr.markForCheck();
        });
    }

    ngOnInit(): void { }

    getVenueById(id: string): void {
        this.venueService.getVenueById(id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (venue) => {
                    this.venue = venue.data;
                    this.cdr.markForCheck();
                },
                error: (err) => console.error('Error fetching venue by ID:', err)
            });
    }

    editCourt(court: any): void {
        this.selectedCourt = court;
        this.drawerOpened = true;
    }

    delete(id: string): void {
        const dialogRef = this._fuseConfirmationService.open({
            title: "Remove Court",
            message: "Are you sure you want to remove this Court permanently? <span class=\"font-medium\">This action cannot be undone!</span>",
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.courtsService.delete(this.venueId, id)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: () => {
                            this.loadAllCourts();
                            this.cdr.markForCheck();
                        },
                        error: (err) => console.error('Error deleting court:', err)
                    });
            }
        });
    }

    /**
     * Load all courts and fetch sport logos
     */
    loadAllCourts(): void {
        this.isLoading = true; // Start loading
        this.courtsService.getAllCourts(
            this.venueId,
            this.pageIndex + 1,
            this.pageSize,
            this.sortBy
        )
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap((courts: any) => {
                    this.totalItems = courts?.totalResults ?? 0;
                    if (!courts?.results?.length) {
                        this.recentTransactionsDataSource.data = [];
                        this.isLoading = false; // Stop loading when no courts
                        this.cdr.markForCheck();
                        return [];
                    }
                    const courtsWithLogo$ = courts.results.map((court: any) =>
                        this.courtsService.getSportByName(court.sports).pipe(
                            map((sportData: any) => ({
                                ...court,
                                sportLogo: sportData?.logo || ''
                            }))
                        )
                    );
                    return forkJoin(courtsWithLogo$);
                })
            )
            .subscribe({
                next: (courtsWithLogos) => {
                    this.recentTransactionsDataSource.data = courtsWithLogos.map(court => ({
                        id: court._id,
                        sports: court.sports,
                        sportLogo: court.sportLogo,
                        status: court.status,
                        name: court.name,
                        courtType: court.courtType,
                        surfaceType: court.surfaceType,
                        airConditioned: court.airConditioned,
                        minCapacity: court.minCapacity,
                        maxCapacity: court.maxCapacity,
                        pricePerHour: court.pricePerHour,
                        pricingRules: court.pricingRules || [],
                        pictures: court.pictures
                    }));
                    this.isLoading = false; // Stop loading after data is loaded
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    console.error('Error fetching courts or logos:', err);
                    this.recentTransactionsDataSource.data = [];
                    this.isLoading = false; // Stop loading on error
                    this.cdr.markForCheck();
                }
            });
    }

    getStartingPrice(court: any): number {
        const intervalPrices = Array.isArray(court?.pricingRules)
            ? court.pricingRules.map((rule: any) => Number(rule.pricePerHour))
            : [];
        return Math.min(Number(court?.pricePerHour || 0), ...intervalPrices);
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.loadAllCourts();
    }

    onSortChange(sort: Sort): void {
        this.pageIndex = 0;
        this.sortBy = sort.direction ? `${sort.active}:${sort.direction}` : 'name:asc';
        this.loadAllCourts();
    }

    onCourtAdded(): void {
        this.drawerOpened = false;
        this.loadAllCourts();
    }

    onBackdropClicked(): void {
        this.drawerOpened = false;
    }

    close($event): void {
        this.drawerOpened = false;
        this.selectedCourt = null;
        this.loadAllCourts();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // Helper method to check if courts are empty
    get hasCourts(): boolean {
        return this.recentTransactionsDataSource.data && this.recentTransactionsDataSource.data.length > 0;
    }
}
